/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {Observable} from 'rxjs';
import {SpecialOrderResultModel} from '../contracts/special-order-result.model';
import {SpecialOrderCommandModel} from '../contracts/special-order-command.model';
import {SpecialOrderMaterialValidationModel} from '../contracts/special-order-material-validation.model';
import {SpecialOrderPositionValidationResultModel} from '../contracts/special-order-position-validation-result.model';
import {SpecialOrderLeadTimeValidationModel} from '../contracts/special-order-lead-time-validation.model';
import {SpecialOrderLeadTimeValidationResultModel} from '../contracts/special-order-lead-time-validation-result.model';
import {SpecialOrderMaterialBulkOperationCommandModel} from '../contracts/special-order-material-bulk-operation-command-model';
import {MultiStatusModel} from '../../generic/contracts/multi-status.model';
import {SpecialOrderQueryModel} from '../contracts/special-order-query.model';
import {SpecialOrderConfigModel} from '../contracts/special-order-config.model';
import {Cacheable} from '../../../decorators/cacheable.decorator';
import {SpecialOrderReportQueryModel} from '../contracts/special-order-report-query.model';
import {SpecialOrderReportResultModel} from '../contracts/special-order-report-result.model';
import {SpecialOrderDashboardModel} from '../contracts/special-order-dashboard.model';


@Injectable({
    providedIn: 'root'
})
export class SpecialOrderService {

    private specialOrderDashboardUrl = `${environment.specialOrdersBaseUrl}/api/specialOrder/dashboard`;
    private specialOrderSearchUrl = `${environment.specialOrdersBaseUrl}/api/specialOrder/search`;
    private specialOrderUrl = `${environment.specialOrdersBaseUrl}/api/specialOrder`;
    private specialOrderReportSearchUrl = `${environment.specialOrdersBaseUrl}/api/specialOrder/report/search`;
    private httpOptions = {
        headers: new HttpHeaders({
            'accept': 'application/json',
            'Content-Type': 'application/json',
        })
    };

    constructor(private http: HttpClient) {
    }

    @Cacheable()
    getSpecialOrderConfig(): Observable<SpecialOrderConfigModel> {
        return this.http.get<SpecialOrderConfigModel>(`${this.specialOrderUrl}/config`);
    }

    /**
     * Gets the dashboard of SPOs from the BE
     */
    getDashboardSPOs(): Observable<SpecialOrderDashboardModel> {
        return this.http.get<SpecialOrderDashboardModel>(this.specialOrderDashboardUrl);
    }

    /**
     * Gets SPOs from the BE
     * @param specialOrderQuery the special order query data
     */
    getSpecialOrders(specialOrderQuery: SpecialOrderQueryModel): Observable<SpecialOrderResultModel> {
        return this.http.post<SpecialOrderResultModel>(this.specialOrderSearchUrl, specialOrderQuery);
    }

    /**
     * Deletes a Draft SPO
     * @param spoNumber The number of the SPO to delete
     */
    deleteSpecialOrder(spoNumber: string): Observable<void> {
        return this.http.delete<void>(`${this.specialOrderUrl}/${spoNumber}`);
    }

    /**
     * Updates a Draft SPO
     * @param specialOrderNumber The number of the SPO to update
     * @param specialOrder The new special order object
     */
    updateSpo(specialOrderNumber: string, specialOrder: SpecialOrderCommandModel): Observable<Object> {
        return this.http.put<SpecialOrderCommandModel>(`${this.specialOrderUrl}/${specialOrderNumber}`, specialOrder);
    }

    /**
     * Updates materials in Open SPO
     * @param specialOrderNumber The number of the SPO
     * @param positions The positions of the materials to update
     */
    updateSpecialOrderMaterials(specialOrderNumber: string,
                                positions: SpecialOrderMaterialBulkOperationCommandModel): Observable<HttpResponse<MultiStatusModel>> {
        return this.http.put<MultiStatusModel>(`${this.specialOrderUrl}/${specialOrderNumber}/specialOrderMaterial/update`, positions, {
            ...this.httpOptions,
            observe: 'response'
        });
    }

    /**
     * This method validates the special order materials.
     *
     * @param validationModel the Special Order material validation model.
     * @return the report response.
     */
    validateSpecialOrderMaterials(validationModel: SpecialOrderMaterialValidationModel):
            Observable<SpecialOrderPositionValidationResultModel[]> {
        return this.http.post<SpecialOrderPositionValidationResultModel[]>(`${this.specialOrderUrl}/material/validate`, validationModel);
    }

    /**
     * This method validates the special order requested date and retrieves the lead time
     *
     * @param validationModel the Special Order Lead Time validation model {@link SpecialOrderLeadTimeValidationModel}
     * @return the report response {@link SpecialOrderLeadTimeValidationResultModel}
     */
    validateSpecialOrderRequestDateAndLeadTime(validationModel: SpecialOrderLeadTimeValidationModel):
            Observable<SpecialOrderLeadTimeValidationResultModel> {
        return this.http.post<SpecialOrderLeadTimeValidationResultModel>(`${this.specialOrderUrl}/requestedDate/validate`, validationModel);
    }

    getSpecialOrdersReport(queryModel: SpecialOrderReportQueryModel):
            Observable<SpecialOrderReportResultModel> {
        return this.http.post<SpecialOrderReportResultModel>(this.specialOrderReportSearchUrl, queryModel);
    }
}
